import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import {
  Badge, EmptyState, PageLoading, SearchInput, Select,
} from '../../components/ui';

type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

const STATUS_TONE: Record<string, BadgeTone> = {
  pending: 'warning',
  confirmed: 'info',
  dispatched: 'info',
  delivered: 'success',
  cancelled: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  dispatched: 'Dispatched',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

interface SecondaryOrder {
  id: string;
  orderNo: string;
  customerName: string;
  routeName: string | null;
  repName: string | null;
  orderDate: string;
  itemCount: number;
  totalAmount: number;
  status: string;
  items?: { productName: string; code: string; quantity: number; unit: string; price: number; total: number }[];
}

/**
 * Secondary orders — the orders reps booked against retailers in this
 * distributor's territory.
 *
 * The distributor can see (but not modify) these orders. They are the
 * demand signal the distributor uses to decide what to order from the
 * company via primary orders.
 */
export default function DistributorOrders() {
  const toast = useToast();
  const [orders, setOrders] = useState<SecondaryOrder[] | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState<SecondaryOrder | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ orders: SecondaryOrder[] }>('/distributor/secondary-orders');
      setOrders(res.orders);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load orders.');
      setOrders([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (orders ?? [])
      .filter((o) => !statusFilter || o.status === statusFilter)
      .filter((o) => !needle
        || o.orderNo.toLowerCase().includes(needle)
        || o.customerName.toLowerCase().includes(needle)
        || o.repName?.toLowerCase().includes(needle)
        || o.routeName?.toLowerCase().includes(needle));
  }, [orders, search, statusFilter]);

  const totalValue = visible
    .filter((o) => o.status !== 'cancelled')
    .reduce((n, o) => n + o.totalAmount, 0);

  return (
    <>
      <header className="topbar">
        <div>
          <h1>Secondary orders</h1>
          <p className="topbar-sub">Orders placed by reps against your retailers.</p>
        </div>
      </header>

      <div className="page col gap-16">
        <div className="toolbar between">
          <div className="row gap-8">
            <SearchInput value={search} onChange={setSearch} placeholder="Order, customer or rep" />
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status">
              <option value="">Any status</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Select>
          </div>
          {orders ? (
            <span className="small faint">
              {visible.length} order{visible.length === 1 ? '' : 's'} · ₹{(totalValue / 100).toLocaleString('en-IN')}
            </span>
          ) : null}
        </div>

        {!orders ? <PageLoading /> : visible.length === 0 ? (
          <div className="card">
            <EmptyState
              icon="list"
              title={search || statusFilter ? 'No orders match that' : 'No secondary orders yet'}
              body={search || statusFilter
                ? 'Try a different search or status.'
                : 'Orders placed by salesmen against your retailers will appear here.'}
            />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Retailer</th>
                    <th>Area</th>
                    <th>Rep</th>
                    <th>Date</th>
                    <th className="num">Lines</th>
                    <th className="num">Value</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((order) => (
                    <tr
                      key={order.id}
                      className="clickable"
                      onClick={() => setDetail(order)}
                    >
                      <td className="mono small nowrap">{order.orderNo}</td>
                      <td className="primary">{order.customerName}</td>
                      <td>{order.routeName ?? '—'}</td>
                      <td className="nowrap">{order.repName ?? '—'}</td>
                      <td className="nowrap">{order.orderDate}</td>
                      <td className="num">{order.itemCount}</td>
                      <td className="num primary">₹{(order.totalAmount / 100).toLocaleString('en-IN')}</td>
                      <td>
                        <Badge tone={STATUS_TONE[order.status] ?? 'neutral'}>
                          {STATUS_LABEL[order.status] ?? order.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {detail ? (
        <OrderDetailModal order={detail} onClose={() => setDetail(null)} />
      ) : null}
    </>
  );
}

function OrderDetailModal({ order, onClose }: { order: SecondaryOrder; onClose: () => void }) {
  const toast = useToast();
  const [full, setFull] = useState<SecondaryOrder | null>(null);

  useEffect(() => {
    api.get<SecondaryOrder>(`/distributor/secondary-orders/${order.id}`)
      .then(setFull)
      .catch((err) => {
        toast.error(err instanceof ApiClientError ? err.message : 'Could not load order.');
      });
  }, [order.id, toast]);

  return (
    <Modal title={`Order ${order.orderNo}`} onClose={onClose} wide>
      <div className="col gap-16">
        <div className="row gap-16" style={{ flexWrap: 'wrap' }}>
          <div><span className="small faint">Retailer</span><div>{order.customerName}</div></div>
          <div><span className="small faint">Area</span><div>{order.routeName ?? '—'}</div></div>
          <div><span className="small faint">Rep</span><div>{order.repName ?? '—'}</div></div>
          <div><span className="small faint">Date</span><div>{order.orderDate}</div></div>
          <div>
            <span className="small faint">Status</span>
            <div>
              <Badge tone={STATUS_TONE[order.status] ?? 'neutral'}>
                {STATUS_LABEL[order.status] ?? order.status}
              </Badge>
            </div>
          </div>
        </div>

        {!full ? <PageLoading /> : full.items && full.items.length > 0 ? (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Code</th>
                  <th className="num">Qty</th>
                  <th>Unit</th>
                  <th className="num">Price</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {full.items.map((item, i) => (
                  <tr key={i}>
                    <td className="primary">{item.productName}</td>
                    <td className="mono small">{item.code}</td>
                    <td className="num">{item.quantity}</td>
                    <td>{item.unit}</td>
                    <td className="num">₹{(item.price / 100).toLocaleString('en-IN')}</td>
                    <td className="num primary">₹{(item.total / 100).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>Total</td>
                  <td className="num primary" style={{ fontWeight: 600 }}>
                    ₹{(order.totalAmount / 100).toLocaleString('en-IN')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="faint">No line items.</p>
        )}
      </div>
    </Modal>
  );
}
